import { GridBaseDataProvider } from '../../../../../../../../grid/GridBaseDataProvider';
import { GetMissingPatchesResponse, GetPatchesGridItems, PatchesColumnIndexes, PatchesColumnKeys } from '../types';
import { GetRowsRequest, SetFiltersRequest } from '../../../../../../../../grid/types/common';
import { ConsoleRequest, DataProviderType } from '../../../../../../../../utils/request';
import { TaskTarget } from '../../../../../../types';

export class PatchesGridDataProvider extends GridBaseDataProvider<PatchesColumnIndexes, PatchesColumnKeys> {
  public targets: Array<TaskTarget>;
  private readRecordsPromise?: Promise<GetMissingPatchesResponse>;
  private backEndResponseProcessed: boolean;

  constructor(public dependencies: any) {
    super();
  }

  async getSummary(request: GetRowsRequest<PatchesColumnIndexes, PatchesColumnKeys>): Promise<any> {
    if (!this.readRecordsPromise) {
      this.readRecordsPromise = this.readRecords(request);
    }

    const response = await this.readRecordsPromise;

    this.deletePromiseIfProcessed();

    const noOfItems = response.total;
    return {
      numItems: noOfItems,
      summary: this.dependencies.translateFn('{noOfItems} items', {
        noOfItems: noOfItems,
      }),
    };
  }

  async getRows(request: GetRowsRequest<PatchesColumnIndexes, PatchesColumnKeys>): Promise<GetPatchesGridItems> {
    if (!this.readRecordsPromise) {
      // this should never occur; the promise is created in getSummary which is always (even when loading async) called
      // before getRows
      throw new Error('Could not fetch rows.');
    }

    const response = await this.readRecordsPromise;

    this.deletePromiseIfProcessed();

    return { rawEvents: response.records };
  }

  private async readRecords(
    request: GetRowsRequest<PatchesColumnIndexes, PatchesColumnKeys>
  ): Promise<GetMissingPatchesResponse> {
    let response;

    const payload = ConsoleRequest.mapGridRequestToPayload(request, this.dependencies.getTimezone());
    payload.targets = this.targets;

    response = await ConsoleRequest.createRequest<GetMissingPatchesResponse>({
      dataProviderType: DataProviderType.CORE,
      payload,
      endpoint: 'PatchesCatalog',
      method: 'getMissingPatchesForTargets',
    });

    return response;
  }

  /**
   * The purpose of this method is to remove the fulfilled (readRecords) promise once it's response has been processed
   * by both getSummary() and getRows().
   * The method is called in both of these methods. The first call will simply set "backEndResponseProcessed" on true
   * while the second call will delete the fulfilled promise.
   *
   * @private
   */
  private deletePromiseIfProcessed(): void {
    if (this.backEndResponseProcessed) {
      delete this.readRecordsPromise;
      // reset value for next backend request (changed filters / refresh grid)
      this.backEndResponseProcessed = false;
      return;
    }

    this.backEndResponseProcessed = true;
  }

  async setFilters(request: SetFiltersRequest<PatchesColumnKeys>): Promise<{} | undefined> {
    return {};
  }
}
